// lib/sanitize.js - HTML Sanitization Utility
// Install: npm install dompurify isomorphic-dompurify

import DOMPurify from "dompurify";
import { convert as htmlToText } from "html-to-text";

const hasSanitizeFn =
  DOMPurify && typeof DOMPurify.sanitize === "function";

const toPlainTextFallback = (dirty) => {
  const text = htmlToText(dirty, {
    wordwrap: false,
    selectors: [
      { selector: "a", options: { ignoreHref: true } },
      { selector: "img", format: "skip" },
    ],
  });

  return String(text || "").replace(/\s+/g, " ").trim();
};

const getPurifier = () => {
  if (typeof window === "undefined") return null;
  if (typeof DOMPurify?.sanitize === "function") return DOMPurify;
  if (typeof DOMPurify?.default?.sanitize === "function") return DOMPurify.default;
  return null;
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - HTML string to sanitize
 * @param {object} options - DOMPurify configuration options
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = (dirty, options = {}) => {
  if (!dirty || typeof dirty !== "string") return "";

  const defaultOptions = {
    ALLOWED_TAGS: [
      "p",
      "br",
      "strong",
      "em",
      "u",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "ul",
      "ol",
      "li",
      "a",
      "img",
      "blockquote",
      "table",
      "thead",
      "tbody",
      "tr",
      "th",
      "td",
      "div",
      "span",
    ],
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "title",
      "class",
      "id",
      "target",
      "rel",
    ],
    ALLOW_DATA_ATTR: false,
  };

  const config = { ...defaultOptions, ...options };
  const purifier = getPurifier();
  if (purifier) {
    return purifier.sanitize(dirty, config);
  }
  if (hasSanitizeFn) {
    return DOMPurify.sanitize(dirty, config);
  }
  return toPlainTextFallback(dirty);
};

/**
 * Sanitize plain text (remove all HTML tags)
 * @param {string} dirty - Text with potential HTML
 * @returns {string} - Plain text
 */
export const sanitizeText = (dirty) => {
  if (!dirty || typeof dirty !== "string") return "";

  const purifier = getPurifier();
  if (purifier) {
    return purifier.sanitize(dirty, { ALLOWED_TAGS: [] });
  }
  if (hasSanitizeFn) {
    return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [] });
  }
  return toPlainTextFallback(dirty);
};

/**
 * Create safe HTML component props
 * @param {string} html - HTML content
 * @returns {object} - Props for dangerouslySetInnerHTML
 */
export const createSafeHtml = (html) => {
  return {
    __html: sanitizeHtml(html),
  };
};
